import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getUserLog} from 'src/openapi/admin/apis/UserLogApi';
import {useAppDispatch} from 'src/store/hooks';

import {userAuditLogStateSelector} from '../slices/user-audit-log-state.slice';
import {
    userAuditLogActions,
    userAuditLogDataSelector,
    userAuditLogResultSelector,
} from '../slices/user-audit-log.slice';

import type {UserAuditLogCriteria} from '../typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {AccountLogsPage} from 'src/openapi/admin/models/AccountLogsPage';


type GetAccountAuditLogCacheKeys = CacheKeys<undefined, undefined, UserAuditLogCriteria>;

type Props = {
    userId: number,
};

export const useGetUserAuditLog = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetAccountAuditLogCacheKeys) => apiDispatch(
        getUserLog({
            userId,
            userLogsPageRequest: {
                pagination: {
                    pageSize: cacheKeys.pagination.pageSize,
                    currentPage: cacheKeys.pagination.currentPage,
                },
                actionType: cacheKeys.filters?.actionType as string | undefined,
                categories: cacheKeys.filters?.categories as Array<string>,
                startDate: cacheKeys.filters?.startDate as string | undefined,
                endDate: cacheKeys.filters?.endDate as string | undefined,
                searchQuery: cacheKeys.filters?.searchQuery?.slice(0, 200) as string | undefined,
            },
        }),
    ), [userId, apiDispatch]);

    const setLoadedData = useCallback((
        response: AccountLogsPage,
        cacheKeys: GetAccountAuditLogCacheKeys,
    ) => {
        dispatch(userAuditLogActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.events,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: userAuditLogDataSelector,
        stateSelector: userAuditLogStateSelector,
        resultSelector: userAuditLogResultSelector,
        actions: userAuditLogActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
